import { seoArticles } from "@/mocks/articles";

export default function SeoContent() {
  return (
    <section className="bg-background-50 py-16 md:py-24 px-4 md:px-6 lg:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 md:mb-12">
          <span className="text-foreground-600 text-xs md:text-sm font-medium tracking-wide uppercase">
            / Mulebuy Spreadsheet SEO Guide
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          <div className="lg:col-span-7">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-6">
              What Is a Mulebuy Spreadsheet?
            </h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-6">
              A <a href="/blog/what-is-mulebuy-spreadsheet" className="text-primary-600 font-medium hover:underline">mulebuy spreadsheet</a> is a structured digital system used to track, compare, and manage product sourcing information in one centralized sheet. It allows users to track product listings efficiently, compare suppliers and pricing in real time, organize orders in a structured workflow, and reduce manual sourcing errors.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-8">
              This system is widely used in modern e-commerce workflows and resale product tracking. Whether you are a beginner reseller or an experienced buyer, the <strong>mulebuy spreadsheet</strong> framework simplifies complex sourcing into a single, organized view that saves hours every week.
            </p>

            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
              Why Mulebuy Spreadsheet Matters
            </h3>
            <div className="overflow-hidden rounded-xl border border-background-200 mb-8">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-foreground-950 text-white">
                    <th className="text-left px-4 py-3 font-medium">Benefit</th>
                    <th className="text-left px-4 py-3 font-medium">Explanation</th>
                    <th className="text-left px-4 py-3 font-medium">Impact</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-background-50">
                    <td className="px-4 py-3 text-foreground-800 font-medium">Centralized Tracking</td>
                    <td className="px-4 py-3 text-foreground-600">All product data in one sheet</td>
                    <td className="px-4 py-3 text-foreground-600">Saves time</td>
                  </tr>
                  <tr className="bg-background-100">
                    <td className="px-4 py-3 text-foreground-800 font-medium">Price Comparison</td>
                    <td className="px-4 py-3 text-foreground-600">Compare multiple sources</td>
                    <td className="px-4 py-3 text-foreground-600">Reduces cost</td>
                  </tr>
                  <tr className="bg-background-50">
                    <td className="px-4 py-3 text-foreground-800 font-medium">Workflow Automation</td>
                    <td className="px-4 py-3 text-foreground-600">Structured buying process</td>
                    <td className="px-4 py-3 text-foreground-600">Improves efficiency</td>
                  </tr>
                  <tr className="bg-background-100">
                    <td className="px-4 py-3 text-foreground-800 font-medium">Scalability</td>
                    <td className="px-4 py-3 text-foreground-600">Handles large product lists</td>
                    <td className="px-4 py-3 text-foreground-600">Supports growth</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
              Key Features of Mulebuy Spreadsheet System
            </h3>
            <ul className="space-y-3 mb-8">
              {[
                "Real-time product tracking structure",
                "Supplier comparison framework",
                "Organized order management system",
                "Scalable spreadsheet architecture",
                "Beginner-friendly layout with advanced capability",
              ].map((feature) => (
                <li key={feature} className="flex items-start gap-3 text-sm md:text-base text-foreground-700">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-check-line text-primary-500"></i>
                  </div>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
              Why Users Choose Our Mulebuy Spreadsheet Resource Hub
            </h3>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-6">
              Users prefer this <strong>mulebuy spreadsheet</strong> hub because it simplifies complex sourcing systems, provides structured comparison guides, helps users make informed buying decisions, and connects directly to a main resource platform.
            </p>

            <div className="inline-flex items-center gap-2 text-foreground-500 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-primary-500"></div>
              <span>Updated Daily with New Links</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-background-100 rounded-2xl border border-background-200/70 p-5 md:p-6 lg:sticky lg:top-24">
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">SEO Articles</h3>
              <div className="space-y-3">
                {seoArticles.map((article) => (
                  <a
                    key={article.slug}
                    href={`/blog/${article.slug}`}
                    className="flex gap-3 p-3 rounded-xl bg-background-50 hover:bg-primary-50/40 transition-colors border border-background-200/50 hover:border-primary-200/60 group"
                  >
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-foreground-950 text-sm mb-1 leading-snug line-clamp-2">{article.title}</h4>
                      <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2">{article.description}</p>
                    </div>
                  </a>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-background-200/50">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {[1,2,3,4].map((i) => (
                      <div key={i} className="w-7 h-7 rounded-full bg-secondary-200 border-2 border-background-50 flex items-center justify-center">
                        <i className="ri-user-line text-secondary-700 text-xs"></i>
                      </div>
                    ))}
                  </div>
                  <span className="text-foreground-600 text-xs">Join 10,000+ resellers</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}